import React from "react";

/**
 * Diagram: styczna + promień + odcinek OP
 *
 * Tryby:
 * - teoria: showValues={false} → etykiety OP, OT, PT
 * - zadania: showValues={true}  → liczby r, d, „PT = ?”
 */
export default function CircleTangentLengthDiagram({
  // dane liczbowe (zadania)
  r = 10,
  d = 26,

  // tryb
  showValues = true,

  // etykiety (teoria)
  labelOP = "OP",
  labelOT = "OT",
  labelPT = "PT",

  // kadr
  width = 720,
  height = 360,
  padding = 28,

  // geometria
  circleR = 120,
  rightReserve = 140,
  topGap = 8,

  // etykiety
  labelOffset = 12,
}) {
  const W = width;
  const H = height;

  // skala
  const rUnits = Math.max(1, Number(r) || 1);
  const dUnits = Math.max(rUnits + 1, Number(d) || (rUnits + 1));

  const R = circleR;
  const scale = R / rUnits;
  const OPpx = dUnits * scale;
  const PTpx = Math.sqrt(Math.max(0, OPpx * OPpx - R * R));

  // pozycjonowanie
  const desiredT = padding + topGap;
  const cy = Math.max(
    padding + R,
    Math.min(desiredT + R, H - padding - R)
  );

  const cxMin = padding + R;
  const cxMaxByP = W - padding - rightReserve - PTpx;
  const cx = Math.max(cxMin, cxMaxByP);

  const O = { x: cx, y: cy };
  const T = { x: cx, y: cy - R };
  const P = { x: cx + PTpx, y: T.y };

  const COLORS = {
    accent: "#f59e0b",
    radius: "#7c3aed",
    tangent: "#10b981",
    base: "currentColor",
  };

  const Label = ({ x, y, dx = 0, dy = 0, anchor = "middle", children }) => (
    <text
      x={x + dx}
      y={y + dy}
      fill={COLORS.accent}
      fontSize="14"
      textAnchor={anchor}
      dominantBaseline="middle"
      pointerEvents="none"
    >
      {children}
    </text>
  );

  const mid = (A, B) => ({ x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 });

  // marker kąta prostego
  const markR = 14;
  const markPath = `
    M ${T.x + markR} ${T.y}
    A ${markR} ${markR} 0 0 1 ${T.x} ${T.y + markR}
  `;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      {/* okrąg */}
      <circle cx={O.x} cy={O.y} r={R} fill="none" stroke={COLORS.base} strokeWidth="2" />

      {/* styczna */}
      <line
        x1={O.x - 0.6 * W}
        y1={T.y}
        x2={P.x + 0.25 * W}
        y2={T.y}
        stroke={COLORS.tangent}
        strokeWidth="3"
      />

      {/* odcinki */}
      <line x1={O.x} y1={O.y} x2={T.x} y2={T.y} stroke={COLORS.radius} strokeWidth="3" />
      <line x1={O.x} y1={O.y} x2={P.x} y2={P.y} stroke={COLORS.base} strokeWidth="2.4" />
      <line x1={T.x} y1={T.y} x2={P.x} y2={P.y} stroke={COLORS.tangent} strokeWidth="4" />

      {/* punkty */}
      <circle cx={O.x} cy={O.y} r="3.2" fill={COLORS.base} />
      <circle cx={T.x} cy={T.y} r="4.2" fill={COLORS.base} />
      <circle cx={P.x} cy={P.y} r="4.2" fill={COLORS.base} />

      {/* kąt prosty */}
      <path d={markPath} fill="none" stroke={COLORS.base} strokeWidth="2" />

      {/* etykiety punktów */}
      <Label x={O.x} y={O.y} dx={-10} anchor="end">O</Label>
      <Label x={T.x} y={T.y} dy={-12}>T</Label>
      <Label x={P.x} y={P.y} dy={-12}>P</Label>

      {/* etykiety długości */}
      <Label x={mid(O, T).x} y={mid(O, T).y} dx={-labelOffset} anchor="end">
        {showValues ? r : labelOT}
      </Label>

      <Label x={mid(O, P).x} y={mid(O, P).y + labelOffset}>
        {showValues ? d : labelOP}
      </Label>

      <Label x={mid(T, P).x} y={mid(T, P).y} dy={-labelOffset}>
        {showValues ? "PT = ?" : labelPT}
      </Label>
    </svg>
  );
}
